import type { Track } from 'livekit-client';
import { VideoTrackView } from './CallVideo';

const ScreenShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="m17 8 5-5" />
    <path d="M17 3h5v5" />
  </svg>
);

/**
 * Share or stop sharing this side's screen, in the same control-column shape
 * as Mute. Given only where the call can carry a screen and this browser can
 * capture one (see canShareScreen).
 */
export function ScreenShareControl({ sharing, busy = false, onToggle }: {
  sharing: boolean;
  busy?: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="cs-control">
      <button
        type="button"
        className={`cs-btn${sharing ? ' cs-btn--muted' : ''}`}
        onClick={onToggle}
        disabled={busy}
        aria-pressed={sharing}
        aria-label={sharing ? 'Stop sharing your screen' : 'Share your screen'}
      >
        <ScreenShareIcon />
      </button>
      <span className="cs-control-label">{sharing ? 'Stop share' : 'Share'}</span>
    </div>
  );
}

/** While this side shares: says so plainly, with Stop right there. */
export function ScreenShareBanner({ onStop }: { onStop?: () => void }) {
  return (
    <div className="cs-share-banner" role="status">
      <ScreenShareIcon />
      <span>You&apos;re sharing your screen</span>
      {onStop && (
        <button type="button" className="cs-share-stop" onClick={onStop} aria-label="Stop sharing">
          Stop
        </button>
      )}
    </div>
  );
}

/** Someone's shared screen, whole (contain, never cropped), with whose it is. */
export function RemoteScreenView({ track, name, className }: {
  track: Track;
  name: string | null;
  className?: string;
}) {
  return (
    <div className={`cs-screen ${className ?? ''}`.trim()} data-testid="call-remote-screen">
      <VideoTrackView track={track} className="cs-screen-video" />
      <span className="cs-screen-label">{name ? `${name}'s screen` : 'Shared screen'}</span>
    </div>
  );
}
